import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Tickets core API — list, detail, create/update, comments, attachments,
 * logs, search, export, unassigned. Field names are verbatim v4 backend.
 *
 * Filter encoding: `page, page_size, sort_by, sort_order, q` go as plain
 * query params; every other filter field is wrapped into a base64-encoded
 * JSON `filters` param via `encodeTicketFilters`. This replicates
 * `transformTicketPayload` from the legacy module exactly.
 */

/* ── Re-export shared enums so UI agents don't reach into customer-tickets ── */
export type { TicketStatus, TicketPriority } from '@/api/customer-tickets';

/* ── Types (ported verbatim from legacy types/response.ts) ──────────────── */

export type TicketTypeGroup =
  | 'support_request'
  | 'error_service'
  | 'service_request'
  | 'service'
  | 'meeting'
  | 'change'
  | 'internal';

export type TicketSecurityTag = 'none' | 'security' | 'service' | 'both';

export type NotificationStatus = 'pending' | 'sent' | 'failed' | null;

export interface TicketAttachment {
  ticket_attactment_name: string;
  ticket_attachment_path: string;
  ticket_attachment_type: string;
  ticket_attachment_size: number;
  ticket_id: number;
}

export interface TicketAttachments {
  ticket_attactment_id: number;
  ticket_attactment_name: string;
  ticket_attachment_path: string;
  ticket_attachment_type: string;
  ticket_attachment_size: number;
  ticket_id: number;
  employee_id: number;
  employee_name: string;
  created_at: string;
  updated_at: string;
}

export interface Ticket {
  ticket_id: number;
  created_by_name: string;
  company_name: string;
  ticket_status: string;
  ticket_priority: string;
  ticket_type_id: number;
  ticket_type_title: string;
  ticket_type_key: string;
  ticket_type_group?: string;
  ticket_request_id: number;
  /* Legacy types declare string[] for these, but the live detail endpoint returns plain strings. */
  ticket_request_title: string | string[];
  ticket_source: string;
  ticket_content: string;
  ticket_note: string;
  ticket_product_name: string | string[];
  ticket_customer_feedback: string;
  ticket_requested_at: string;
  ticket_customer_rating: string;
  ticket_deadline_at: string;
  employee_id: number;
  ticket_technical_assignees: number[];
  ticket_technical_assignee_names: { employee_name: string }[];
  ticket_cs_assignees: number[];
  ticket_cs_assignee_names: { employee_name: string }[];
  ticket_sale_assignees: number[];
  ticket_sale_assignee_names: { employee_name: string }[];
  ticket_employee_assignees: number[];
  ticket_department: number;
  ticket_solution: string;
  ticket_estimated_completion_date: string;
  ticket_resolved_at: string;
  ticket_meeting_type: string;
  ticket_meeting_via: string;
  ticket_meeting_channel: string;
  customer_id: number;
  customer_name: string;
  service_id: number;
  service_startdate: string;
  service_duedate: string;
  employee_name: string;
  service_name: string;
  service_status: string;
  /* Legacy types declare string[] but the live detail endpoint returns a plain string. */
  product_group_name: string | string[];
  ticket_time_repply: string;
  ticket_time_response: string;
  ticket_time_release: string;
  ticket_attachment?: File[];
  attachment?: TicketAttachment;
  ticket_response_late: boolean;
  ticket_owner_id: number;
  ticket_owner_type: string;
  ticket_root_cause: string;
  ticket_remedial_measures: string;
  ticket_avoid: string;
  ticket_affected: string;
  ticket_incident_type: string;
  ticket_security_tag: TicketSecurityTag;
  ticket_marjorable: boolean[];
  ticket_internal_root_cause: string;
  ticket_internal_remedial_measures: string;
  ticket_internal_avoid: string;
  ticket_groups: string[];
  ticket_scope: string;
  created_at: string;
  updated_at: string;
  notification_status?: NotificationStatus;
}

export interface Metadata {
  new: Record<string, string | unknown[]>;
  old: Record<string, string | unknown[]>;
}

export interface TicketCreatedMetadata {
  action: string;
  ticket_id: number;
}

export interface TicketLogs {
  ticket_log_id: number;
  ticket_log_action: string;
  ticket_id: number;
  ticket_log_change: Metadata | TicketCreatedMetadata | null;
  created_at: string;
  updated_at: string;
  employee_id: number;
  employee_name: string;
  ticket_log_content: string;
  ticket_log_title: string;
}

/**
 * Actual shape returned by `GET /tickets/:id/logs` (verified live).
 * The backend returns the ActivityType shape, not the TicketLogs legacy type.
 */
export interface TicketActivityLog {
  created_at: string;
  employee_name: string;
  action: string;
  description: string;
  metadata?: unknown;
  task_id?: string;
  status?: 'failed' | 'resolved' | 'unresolved';
  resolved_at?: string;
}

/* ── Payload types (ported verbatim from legacy types/payload.ts) ─────────── */

export interface TicketAttachmentPayload {
  ticket_attactment_name: string;
  ticket_attachment_path: string;
  ticket_attachment_type: string;
  ticket_attachment_size: number;
}

export interface TicketDowntimePayload {
  downtime_start: string;
  downtime_end?: string;
  downtime_cause?: string;
  downtime_description?: string;
  root_cause?: string;
  counts_against_sla?: boolean;
}

export interface TicketPayload {
  ticket_status: string;
  ticket_priority: string;
  ticket_type_id: number;
  ticket_request_id: number;
  ticket_content: string;
  ticket_requested_at: string;
  employee_id: number;
  ticket_technical_assignees?: number[];
  ticket_cs_assignees?: number[];
  ticket_sale_assignees?: number[];
  ticket_estimated_completion_date?: string | null;
  service_suspend_until?: string | null;
  ticket_meeting_type?: string;
  ticket_meeting_channel?: string;
  ticket_meeting_via?: string;
  ticket_meeting_purpose?: string;
  customer_journey_id?: number;
  customer_id: number;
  service_id: number;
  ticket_id: number;
  ticket_time_repply: string | null;
  ticket_time_release: string | null;
  ticket_affected: string;
  ticket_avoid: string;
  ticket_root_cause: string;
  ticket_remedial_measures: string;
  ticket_internal_root_cause?: string;
  ticket_internal_remedial_measures?: string;
  ticket_internal_avoid?: string;
  ticket_marjorable: boolean;
  service_startdate: string | null;
  service_duedate: string | null;
  ticket_attachment?: File[];
  attachment?: TicketAttachment;
  ticket_product_name: string;
  /* IT-ISMS classification tag; legacy create form sends it (spread via ...data). */
  ticket_security_tag?: string;
}

export interface TicketUpdatePayload {
  ticket_id: number;
  ticket_status: string;
  ticket_priority: string;
  ticket_type_id: number;
  ticket_request_id: number;
  ticket_source: string;
  ticket_security_tag?: string;
  ticket_content: string;
  ticket_note?: string;
  ticket_product_name?: string;
  ticket_customer_feedback?: string;
  ticket_requested_at: string;
  ticket_time_response: string;
  ticket_time_repply: string;
  ticket_time_release: string;
  ticket_technical_assignees?: number[];
  ticket_cs_assignees?: number[];
  ticket_sale_assignees?: number[];
  ticket_employee_assignees?: number[];
  ticket_department: string;
  ticket_solution?: string;
  ticket_estimated_completion_date?: string;
  ticket_resolved_at: string;
  ticket_meeting_type?: string;
  ticket_meeting_via?: string;
  ticket_meeting_channel?: string;
  ticket_meeting_purpose?: string;
  customer_id: number;
  service_id: number;
  service_startdate: string;
  service_duedate: string;
  service_suspend_until?: string;
  ticket_internal_root_cause?: string;
  ticket_internal_remedial_measures?: string;
  ticket_internal_avoid?: string;
  ticket_affected?: string;
  ticket_avoid?: string;
  ticket_root_cause?: string;
  ticket_remedial_measures?: string;
  ticket_marjorable?: boolean;
  customer_journey_id?: number;
  creator_id?: number;
  creator_source?: string;
  creator_metadata?: Record<string, unknown>;
}

export interface CreateTicketRequestPayload {
  note: Partial<TicketPayload>;
  attachments?: TicketAttachmentPayload[];
  downtime?: TicketDowntimePayload;
}

export interface UpdateTicketRequestPayload {
  note: Partial<TicketUpdatePayload>;
  attachments?: TicketAttachmentPayload[];
  downtime?: TicketDowntimePayload;
}

export interface TicketCommentPayload {
  ticket_id: number;
  ticket_comment_pinted: string;
  ticket_comment_text: string;
  employee_id: number;
  employee_name: string;
  created_at: string;
  updated_at: string;
  ticket_comment_id: number;
  ticket_comment_author_type?: string;
  ticket_comment_visibility?: string;
}

/* ── List params ─────────────────────────────────────────────────────────── */

/** Fields that travel as plain query params (not inside the base64 filters). */
const BASE_FIELDS = ['page', 'page_size', 'sort_by', 'sort_order', 'q'] as const;

export interface TicketListParams {
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  q?: string;
  /** Pre-encoded base64 filters string (merged with any additional filter fields). */
  filters?: string;
  [key: string]: unknown;
}

export interface TicketLogsParams {
  page?: number;
  page_size?: number;
}

/* ── Filter encoder (exported pure function for unit-testing) ─────────────── */

/**
 * Replicates legacy `transformTicketPayload`. Base fields go as plain params;
 * every other key wraps into `base64(JSON)` as the `filters` query param. If
 * an existing encoded `filters` string is supplied it is merged (decoded first)
 * with the new filter fields before re-encoding.
 */
export function encodeTicketFilters(params: TicketListParams): Record<string, unknown> {
  const baseParams: Record<string, unknown> = {};
  const filterParams: Record<string, unknown> = {};
  let existingFilters: string | null = null;

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;

    if ((BASE_FIELDS as readonly string[]).includes(key)) {
      baseParams[key] = value;
    } else if (key === 'filters') {
      existingFilters = value as string;
    } else {
      filterParams[key] = value;
    }
  }

  if (Object.keys(filterParams).length > 0) {
    let merged: Record<string, unknown> = {};
    if (existingFilters) {
      try {
        merged = JSON.parse(atob(existingFilters));
      } catch {
        // Ignore malformed pre-encoded strings
      }
    }
    merged = { ...merged, ...filterParams };
    baseParams.filters = btoa(JSON.stringify(merged));
  } else if (existingFilters) {
    baseParams.filters = existingFilters;
  }

  return baseParams;
}

/* ── Query keys ──────────────────────────────────────────────────────────── */

export const ticketKeys = {
  all: ['tickets'] as const,
  list: (params: TicketListParams) => ['tickets', 'list', params] as const,
  unassigned: (params: TicketListParams) => ['tickets', 'unassigned', params] as const,
  detail: (id: number) => ['tickets', 'detail', id] as const,
  comments: (id: number) => ['tickets', id, 'comments'] as const,
  attachments: (id: number) => ['tickets', id, 'attachments'] as const,
  logs: (id: number, params: TicketLogsParams) => ['tickets', id, 'logs', params] as const,
  search: (query: string) => ['tickets', 'search', query] as const,
};

/* ── List ────────────────────────────────────────────────────────────────── */

/** `GET /tickets` — paginated list with base64-encoded filter params. */
export function useTickets(params: TicketListParams = {}) {
  return useQuery({
    queryKey: ticketKeys.list(params),
    queryFn: async (): Promise<ResponseArray<Ticket>> => {
      const encoded = encodeTicketFilters(params);
      const res = await dataCentralApi.get<ResponseArray<Ticket>>(
        `/tickets?${qs.stringify(encoded)}`,
      );
      return res.data;
    },
  });
}

/* ── Unassigned ──────────────────────────────────────────────────────────── */

/** `GET /tickets/unassigned` — plain query params, no base64 filters. */
export function useUnassignedTickets(params: TicketListParams = {}) {
  return useQuery({
    queryKey: ticketKeys.unassigned(params),
    queryFn: async (): Promise<ResponseArray<Ticket>> => {
      const res = await dataCentralApi.get<ResponseArray<Ticket>>(
        `/tickets/unassigned?${qs.stringify(params)}`,
      );
      return res.data;
    },
  });
}

/* ── Detail ──────────────────────────────────────────────────────────────── */

/** `GET /tickets/:id` — single ticket record. */
export function useTicket(id: number) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.get<ResponseObject<Ticket>>(`/tickets/${id}`);
      return res.data;
    },
  });
}

/* ── Comments ────────────────────────────────────────────────────────────── */

/** `GET /tickets/:id/comments` */
export function useTicketComments(id: number) {
  return useQuery({
    queryKey: ticketKeys.comments(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<TicketCommentPayload>> => {
      const res = await dataCentralApi.get<ResponseArray<TicketCommentPayload>>(
        `/tickets/${id}/comments`,
      );
      return res.data;
    },
  });
}

/** `POST /tickets/:id/comments` */
export function useCreateTicketComment(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: TicketCommentPayload,
    ): Promise<ResponseArray<TicketCommentPayload>> => {
      const res = await dataCentralApi.post<ResponseArray<TicketCommentPayload>>(
        `/tickets/${ticketId}/comments`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.comments(ticketId) });
    },
  });
}

/** `PUT /tickets/comment/:commentId` — note: SINGULAR `/comment/` path. */
export function useUpdateTicketComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_comment_id,
      ticket_comment_text,
      ticket_id: _,
    }: {
      ticket_comment_id: number;
      ticket_comment_text: string;
      ticket_id: number;
    }): Promise<ResponseObject<TicketCommentPayload>> => {
      const res = await dataCentralApi.put<ResponseObject<TicketCommentPayload>>(
        `/tickets/comment/${ticket_comment_id}`,
        { ticket_comment_text },
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.comments(ticket_id) });
    },
  });
}

/** `DELETE /tickets/comment/:commentId` — note: SINGULAR `/comment/` path. */
export function useDeleteTicketComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_comment_id,
      ticket_id: _,
    }: {
      ticket_comment_id: number;
      ticket_id: number;
    }): Promise<ResponseObject<TicketCommentPayload>> => {
      const res = await dataCentralApi.delete<ResponseObject<TicketCommentPayload>>(
        `/tickets/comment/${ticket_comment_id}`,
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.comments(ticket_id) });
    },
  });
}

/* ── Attachments ─────────────────────────────────────────────────────────── */

/** `GET /tickets/:id/attachments` */
export function useTicketAttachments(id: number) {
  return useQuery({
    queryKey: ticketKeys.attachments(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<TicketAttachments>> => {
      const res = await dataCentralApi.get<ResponseArray<TicketAttachments>>(
        `/tickets/${id}/attachments`,
      );
      return res.data;
    },
  });
}

/* ── Logs ────────────────────────────────────────────────────────────────── */

/** `GET /tickets/:id/logs` — activity log, paginated. Returns ActivityLog shape (verified live). */
export function useTicketLogs(id: number, params: TicketLogsParams = {}) {
  return useQuery({
    queryKey: ticketKeys.logs(id, params),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<TicketActivityLog>> => {
      const res = await dataCentralApi.get<ResponseArray<TicketActivityLog>>(
        `/tickets/${id}/logs?${qs.stringify(params)}`,
      );
      return res.data;
    },
  });
}

/* ── Search ──────────────────────────────────────────────────────────────── */

/** `GET /tickets/search?query=` — for link-ticket picker, returns slim Ticket rows. */
export function useTicketSearch(query: string) {
  return useQuery({
    queryKey: ticketKeys.search(query),
    enabled: query.length > 0,
    queryFn: async (): Promise<ResponseArray<Ticket>> => {
      const res = await dataCentralApi.get<ResponseArray<Ticket>>(
        `/tickets/search?query=${encodeURIComponent(query)}`,
      );
      return res.data;
    },
  });
}

/* ── Create / Update ─────────────────────────────────────────────────────── */

/** `POST /tickets` */
export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CreateTicketRequestPayload,
    ): Promise<ResponseObject<TicketPayload>> => {
      const res = await dataCentralApi.post<ResponseObject<TicketPayload>>('/tickets', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/** `PUT /tickets/:id` — id from `payload.note.ticket_id`. */
export function useUpdateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: UpdateTicketRequestPayload,
    ): Promise<ResponseObject<TicketPayload>> => {
      const id = payload.note.ticket_id;
      const res = await dataCentralApi.put<ResponseObject<TicketPayload>>(
        `/tickets/${id}`,
        payload,
      );
      return res.data;
    },
    onSuccess: (_data, payload) => {
      const id = payload.note.ticket_id;
      if (id) {
        queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
      }
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/* ── Export (blob) ───────────────────────────────────────────────────────── */

/**
 * `GET /tickets/export` — returns an Excel blob. Not a query hook because
 * it's user-triggered (download button); call directly.
 */
export async function exportTickets(params: TicketListParams): Promise<Blob> {
  const encoded = encodeTicketFilters(params);
  const res = await dataCentralApi.get(`/tickets/export?${qs.stringify(encoded)}`, {
    responseType: 'blob',
  });
  return res.data as Blob;
}
